import React, { useEffect, useRef } from 'react';
import { useMap, Rectangle, Marker } from 'react-leaflet';
import L from 'leaflet';
import * as turf from '@turf/turf';

// Label pinned just inside the rectangle's NW corner so the user can
// tell at a glance which part of the map will end up in the rendered
// image. The outer divIcon className is blank to suppress leaflet's
// default white border; all visible styling (incl. mobile-shrink
// breakpoint) lives in `.rect-selected-label` in index.css.
const RECT_LABEL_TEXT = 'Selected Area';
const RECT_LABEL_ICON = L.divIcon({
  html: '<div class="rect-selected-label">' + RECT_LABEL_TEXT + '</div>',
  className: '',
  iconSize: [0, 0],
  iconAnchor: [0, 0],
});

const isValidBounds = (bounds) => {
  return Array.isArray(bounds) && 
         bounds.length === 2 && 
         Array.isArray(bounds[0]) && 
         Array.isArray(bounds[1]) && 
         bounds[0].length === 2 && 
         bounds[1].length === 2 && 
         !bounds.flat().some(isNaN);
};

const computeBoundsFor = (center, km2, aspectRatio) => {
  const areaInMeters = km2 * 1000000;
  const height = Math.sqrt(areaInMeters / aspectRatio);
  const width = height * aspectRatio;
  const southPoint = turf.destination([center.lng, center.lat], height / 2, 180, { units: 'meters' });
  const northPoint = turf.destination([center.lng, center.lat], height / 2, 0, { units: 'meters' });
  const eastPoint = turf.destination([center.lng, center.lat], width / 2, 90, { units: 'meters' });
  const westPoint = turf.destination([center.lng, center.lat], width / 2, -90, { units: 'meters' });
  return [
    [southPoint.geometry.coordinates[1], westPoint.geometry.coordinates[0]],
    [northPoint.geometry.coordinates[1], eastPoint.geometry.coordinates[0]],
  ];
};

const EditableRectangle = ({ bounds, setBounds, km2, aspectRatio, onCenterChange }) => {
  const map = useMap();
  const lastSizeRef = useRef(null);
  // Track previous km2 / aspectRatio so we can distinguish a user-driven
  // change (the input slider, an aspect-ratio swap) from the initial
  // mount. We only want to *actively* resize the rectangle on change,
  // not stomp on restored bounds when the component first appears.
  const prevKm2Ref = useRef(km2);
  const prevAspectRatioRef = useRef(aspectRatio);

  // When km2 or aspectRatio changes after mount, recompute the bounds
  // immediately around the rectangle's own center — without this, the
  // user had to nudge the map (moveend) for the size change to take
  // effect. We pick the rectangle center (not the viewport center) so a
  // slider tick doesn't jump the rectangle to wherever the user happened
  // to pan to between updates.
  useEffect(() => {
    if (prevKm2Ref.current === km2 && prevAspectRatioRef.current === aspectRatio) {
      return;
    }
    prevKm2Ref.current = km2;
    prevAspectRatioRef.current = aspectRatio;
    if (!isValidBounds(bounds)) return;
    const [[s, w], [n, e]] = bounds;
    const center = { lat: (s + n) / 2, lng: (w + e) / 2 };
    const newBounds = computeBoundsFor(center, km2, aspectRatio);
    if (isValidBounds(newBounds)) {
      setBounds(newBounds);
    }
    // bounds intentionally omitted — we want the effect to fire only on
    // km2/aspectRatio change, not on every setBounds round-trip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [km2, aspectRatio]);

  useEffect(() => {
    if (!map) return;

    // Programmatic moves at mount (ViewRestorer's setView, place-search
    // fitBounds, container size hydration) fire moveend within the
    // first few hundred ms. Without an armed guard, those would
    // overwrite restored bounds OR (when bounds is null) auto-create
    // a rectangle before the user has touched anything. The flag flips
    // after a short delay so genuine user pans still come through.
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 600);

    const updateRectangle = () => {
      // Detect a moveend that was triggered by a container size change
      // (e.g. side panel toggling) and ignore it. Without this, the
      // rectangle silently re-centers around the new viewport center every
      // time the map is resized.
      const currentSize = map.getSize();
      if (
        lastSizeRef.current &&
        (lastSizeRef.current.x !== currentSize.x || lastSizeRef.current.y !== currentSize.y)
      ) {
        lastSizeRef.current = currentSize;
        return;
      }
      lastSizeRef.current = currentSize;

      if (!armed) return;

      const center = map.getCenter();
      const newBounds = computeBoundsFor(center, km2, aspectRatio);
      if (isValidBounds(newBounds)) {
        setBounds(newBounds);
        onCenterChange([center.lat, center.lng]);
      }
    };

    lastSizeRef.current = map.getSize();

    map.on('moveend', updateRectangle);
    map.on('zoomend', updateRectangle);

    return () => {
      clearTimeout(armTimer);
      map.off('moveend', updateRectangle);
      map.off('zoomend', updateRectangle);
    };
  }, [map, setBounds, km2, aspectRatio]);

  if (!isValidBounds(bounds)) {
    return null;
  }

  // NW corner of the rectangle. bounds is [[south, west], [north, east]],
  // so NW = (north lat, west lng). The icon's inline `translate(8px,8px)`
  // shifts the box inside the border.
  const nwCorner = [bounds[1][0], bounds[0][1]];

  return (
    <>
      <Rectangle
        bounds={bounds}
        pathOptions={{ color: 'blue', weight: 5, fillOpacity: 0 }}
      />
      <Marker
        position={nwCorner}
        icon={RECT_LABEL_ICON}
        interactive={false}
        keyboard={false}
      />
    </>
  );
};

export default EditableRectangle;